import type { MagentoOrderPrices, Order, OrderItem, Product } from "@jti/magento";
import type {
  CommerceEventBaseData,
  CommerceEventItem,
  SharedData,
  TemplateName,
  TrackingEvents,
  TrackingEventsAdditionalData,
} from "@jti/tracker";

const NO_VALUE = "N/A";
const CARTON_FORMAT = ["Pack", "Mix and Match", "Carton"];

export type CartData = {
  items: Product[];
  prices?: MagentoOrderPrices;
  currency?: {
    code: string;
  };
};

export type OrderData = Order;

export type TrackingData = CartData | OrderData;

type OrderPurchaseSummaryTrackingPayload = {
  trackingEvent: TrackingEvents.TrackingOrderTradeIn | TrackingEvents.TrackingOrderSummaryPurchase;
  purchaseData:
    | TrackingEventsAdditionalData[TrackingEvents.TrackingOrderSummaryPurchase]
    | TrackingEventsAdditionalData[TrackingEvents.TrackingOrderTradeIn];
};

export const isSubscriptionProduct = (product: Product | OrderItem): boolean => {
  const { subscription = 0 } = product;

  if (!subscription) {
    return false;
  }

  return subscription === 1;
};

export const isCartonProduct = (product: Product | OrderItem): boolean => {
  const { subscription_format } = product;

  if (!subscription_format) {
    return false;
  }

  return CARTON_FORMAT.includes(subscription_format);
};

const buildCommerceItemsArray = (arrayWithCartItems?: (Product | OrderItem)[]): CommerceEventItem[] => {
  if (!arrayWithCartItems?.length) {
    return [];
  }

  return arrayWithCartItems.reduce<CommerceEventItem[]>((acc, item, index) => {
    if (!item) {
      return acc;
    }

    const { price, name, sku, quantity } = item;
    const discount = item.price_range?.maximum_price?.discount?.percent_off;

    let itemPrice = price;
    if ("productSalePrice" in item && item.productSalePrice !== undefined && item.productSalePrice !== null) {
      itemPrice = item.productSalePrice;
    }

    const isSubscriptionItem = isSubscriptionProduct(item);
    const isCartonItem = isCartonProduct(item);

    const data: CommerceEventItem = {
      index: Number(index),
      currency: price.currency,
      item_category: item.categories ? item.categories[0]?.name : NO_VALUE,
      item_category2: item.categories ? item.categories[1]?.name : NO_VALUE,
      item_color: item.color_label?.toLowerCase() ?? NO_VALUE,
      item_flavor: String(item.flavor ?? NO_VALUE),
      item_name: name,
      item_sku: sku,
      item_list_name: item.categories ? `${item.categories[0]?.name} > ${item.categories[1]?.name}` : NO_VALUE,
      item_list_id: item.categories ? `${item.categories[0]?.name} > ${item.categories[1]?.name}` : NO_VALUE,
      price: Number(itemPrice.value),
      quantity: Number(quantity),
      discount: discount ?? 0,
      /*
        We need to ensure consistent item_id value throughout checkout process for tracking purposes
        the best candidate to preserve consistency in this case seems to be SKU code
        https://jira.jtiweb.co.uk/browse/DESP-91609
      */
      item_id: sku || name,
      item_brand: "ploom",
      item_coupon: NO_VALUE,
      item_coupon_type: NO_VALUE,
      item_strength: NO_VALUE,
      item_rewards: NO_VALUE,
      purchase_type: isSubscriptionItem ? "subscription" : "one-time",
      carton_format: item?.subscription_format ?? NO_VALUE,
      carton_quantity: isCartonItem ? quantity : NO_VALUE,
    };

    acc.push(data);

    return acc;
  }, []);
};

const getTradeInValue = (orderItems?: (Product | OrderItem)[]) => {
  if (!orderItems) {
    return 0;
  }

  return orderItems.reduce((sum, item) => sum + (item.price?.value ?? 0), 0);
};

export const buildCommerceBaseData = (
  trackingData: TrackingData,
  changedItemValue?: number,
  isTradeInPurchase?: boolean
): CommerceEventBaseData => {
  const isOrderData = "orderId" in trackingData;
  const orderItems = isTradeInPurchase
    ? trackingData.items?.filter((orderItem) => orderItem.tradeIn)
    : trackingData.items;

  let currency: string;
  let value: number;

  if (isOrderData) {
    const orderData = trackingData;
    currency = orderData.prices?.grandTotal.currency || "N/A";
    value = changedItemValue ?? orderData.prices?.grandTotal.value ?? 0;
  } else {
    const cartData = trackingData;
    currency = cartData.currency?.code || cartData.prices?.grandTotal.currency || "N/A";
    value = changedItemValue ?? (cartData.prices ? cartData.prices.grandTotal.value : 0);
  }

  return {
    currency,
    value: isTradeInPurchase ? getTradeInValue(orderItems) : Number(value),
    ecommerce: {
      items: buildCommerceItemsArray(orderItems),
    },
  };
};

export const buildTradeInPurchaseData = ({
  order,
  sharedData,
  isTradeInPurchase,
}: {
  order: Order;
  sharedData: SharedData;
  isTradeInPurchase?: boolean;
}): TrackingEventsAdditionalData[TrackingEvents.TrackingOrderTradeIn] => {
  const { currency, value, ecommerce } = buildCommerceBaseData(order, undefined, isTradeInPurchase);

  const purchaseData: TrackingEventsAdditionalData[TrackingEvents.TrackingOrderTradeIn] = {
    section: sharedData.section,
    checkout_type: sharedData.checkout_type,
    template_name: "order-confirmation",
    currency,
    value,
    ecommerce,
  };

  return purchaseData;
};

export const buildPurchaseData = (
  order: Order,
  sharedData: SharedData
): TrackingEventsAdditionalData[TrackingEvents.TrackingOrderSummaryPurchase] => {
  const { currency, value, ecommerce } = buildCommerceBaseData(order);

  const purchaseData: OrderPurchaseSummaryTrackingPayload["purchaseData"] = {
    section: sharedData.section,
    checkout_type: sharedData.checkout_type,
    template_name: sharedData.template_name,
    currency,
    value,
    ecommerce: {
      ...ecommerce,
      coupon: "N/A",
      shipping: order.shippingMethod?.methodCode ?? "N/A",
      tax: String(order.prices?.appliedTaxes?.[0]?.amount?.value || "N/A"),
      value,
      transaction_id: order.orderId,
    },
  };

  return purchaseData;
};

export const getTemplateName = (pathname: TemplateName): SharedData => {
  return {
    section: pathname,
    template_name: pathname,
    checkout_type: pathname,
  };
};
